// src/utils/handleError.ts
import { Sentry } from '@/lib/sentry';
import { toast } from 'sonner';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import { extractFunctionError } from '@/utils/errorUtils';
import { CORRELATION_HEADER } from '@/clients/correlatedFetch';
import { isServerFailureFault } from '@financialharmony/ui/errors';

interface NormalizedError {
  status?: number;
  message?: string;
  // The response body's `error` field — the backend's ServiceError.errorType.
  errorCode?: string;
  original: unknown;
  // 'warning' for network failures (FunctionsFetchError/FunctionsRelayError) — still
  // reported so a systemic pattern is visible (see below), just not counted as a hard
  // error. 'error' for everything else that gets reported.
  sentryLevel: 'error' | 'warning';
  // FIN-865/AC10: the 8-char reference shown to the user, so they can quote it to support and
  // the team can find the matching edge_function_errors row. Read from the FAILING response's
  // own x-harmony-correlation-id header (serveWithContext.ts echoes it on every response,
  // success or error) — not the response body, and not a last-sent-id store, since either can
  // point at a different in-flight request. Undefined for a network failure (no response ever
  // arrived to read a header from).
  reference?: string;
  // The body's machine-readable `error` code (e.g. INTERNAL_ERROR), when the server sent one.
  errorCode?: string;
}

/**
 * Server faults whose `message` is a fixed developer string, never a sentence for a person
 * (FIN-260: handleError() on the server answers every unmodelled failure with
 * "Internal server error"). The caller's own fallback is shown instead — the same rule
 * packages/ui's userFacingErrorMessage applies for apps/client (FIN-844).
 */
const SERVER_FAULT_ERROR_CODES = new Set(['INTERNAL_ERROR', 'REPOSITORY_ERROR']);

type ErrorExtractor = (err: unknown, preParsedBody?: any) => Promise<NormalizedError | null>;

async function extractSupabaseFunctionError(err: unknown, preParsedBody?: any): Promise<NormalizedError | null> {
  if (!err || typeof err !== 'object') return null;
  const name = (err as any).name;

  // Network failures (FunctionsFetchError/FunctionsRelayError) were previously excluded
  // from Sentry entirely as "not server bugs" — but that's exactly the error class a
  // CORS-preflight rejection surfaces as client-side (issue #616/#662): a 100%
  // reproducible, production-wide regression that generated zero alerts for ~4 days
  // because of this exclusion. Report at 'warning' instead of dropping: a single flaky
  // connection doesn't inflate the hard error rate, but Sentry still groups and counts
  // occurrences per `context` tag, so a systemic spike (many users, same tag, same
  // shape) is now visible and alertable instead of invisible.
  if (name === 'FunctionsFetchError' || name === 'FunctionsRelayError') {
    // No response ever arrived, so there is no header to read a reference from — matches
    // apps/client's referenceOf(), which is likewise undefined for a network failure.
    return { original: err, sentryLevel: 'warning' };
  }

  if (name !== 'FunctionsHttpError') return null;

  const context = (err as any).context;
  if (!context) return null;

  // Read status synchronously — it is a plain property on the Response object
  const status: number = context.status;

  // A Response body can only be read once. Callers that already parsed it (e.g. to check
  // for a known error code like CONSENT_REQUIRED before falling through here) must pass
  // that result as preParsedBody — re-reading via extractFunctionError would silently
  // return null (context.bodyUsed is now true) and discard the real server message.
  const bodyData = preParsedBody !== undefined
    ? preParsedBody
    : (context.bodyUsed ? null : await extractFunctionError(err));
  const message: string | undefined = bodyData?.message ?? undefined;
  const errorCode: string | undefined = typeof bodyData?.error === 'string' ? bodyData.error : undefined;
  const reference: string | undefined = context.headers?.get(CORRELATION_HEADER)?.slice(0, 8) || undefined;

  return { status, message, errorCode, original: err, sentryLevel: 'error', reference };
}

const extractors: ErrorExtractor[] = [extractSupabaseFunctionError];

async function normalize(err: unknown, preParsedBody?: any): Promise<NormalizedError> {
  for (const extractor of extractors) {
    const result = await extractor(err, preParsedBody);
    if (result !== null) return result;
  }
  // Unknown error — treat as unexpected, will be reported to Sentry
  return { original: err, sentryLevel: 'error' };
}

export async function handleError(
  err: unknown,
  fallback: string,
  context: string,
  preParsedBody?: any,
): Promise<void> {
  const normalized = await normalize(err, preParsedBody);

  // 'warning'-level network failures are always reported (at reduced severity — see
  // above). Everything else keeps the original rule: only report real server errors
  // (5xx or no status), not expected 4xx responses the caller already handles.
  const shouldReport =
    normalized.sentryLevel === 'warning' ||
    normalized.status === undefined ||
    normalized.status >= 500;

  const isServerFault = normalized.errorCode !== undefined && SERVER_FAULT_ERROR_CODES.has(normalized.errorCode);
  // Sentry keeps any non-fixed server sentence for grouping; a person never sees a 500's text (FIN-380).
  const loggableMessage = isServerFault ? undefined : normalized.message;
  const userMessage = isServerFailureFault(normalized) ? undefined : loggableMessage;

  if (shouldReport) {
    // A server fault's message is the same fixed string for every failure, so it is useless
    // for Sentry grouping — name the failing action instead (the `context` tag stays as well).
    let errorToCapture: unknown = normalized.original;
    if (loggableMessage) errorToCapture = new Error(loggableMessage);
    else if (isServerFault) errorToCapture = new Error(`${fallback} (${normalized.errorCode})`);
    Sentry.captureException(errorToCapture, {
      level: normalized.sentryLevel,
      tags: { context },
      extra: { status: normalized.status, original: normalized.original },
    });
  }

  const baseMessage = userMessage ?? fallback;
  toast.error(
    normalized.reference
      ? translateOutsideReact('consultantToasts.api.withReference', { message: baseMessage, reference: normalized.reference })
      : baseMessage,
  );
}
